import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, Save, X, ExternalLink, Eye, EyeOff, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { FacebookEmbed } from 'react-social-media-embed';

const firebaseConfig = {
  apiKey: "AIzaSyAGe4tEALPi6kdBnAJo4uf-8omOQ4Nda-0",
  authDomain: "pathway-academy.firebaseapp.com",
  projectId: "pathway-academy",
  storageBucket: "pathway-academy.firebasestorage.app",
  messagingSenderId: "822490806225",
  appId: "1:822490806225:web:218ebd85b334fa140312f0",
  measurementId: "G-43HW36L0TK"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const SocialMediaManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [previewVisible, setPreviewVisible] = useState({});
  const [draggedPost, setDraggedPost] = useState(null);
  const [dragOverPost, setDragOverPost] = useState(null);
  const [formData, setFormData] = useState({
    embedUrl: '',
    platform: 'facebook',
    description: ''
  });
  const navigate = useNavigate();
  const auth = getAuth();

  const togglePreview = (postId) => {
    setPreviewVisible(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin');
      }
    });

    fetchPosts();

    return () => unsubscribe();
  }, [auth, navigate]);

  const fetchPosts = async () => {
    try {
      const q = query(collection(db, 'socialPosts'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const postList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postList);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract URL from iframe embed code or return the URL as-is
  const extractUrlFromInput = (input) => {
    const trimmedInput = input.trim();
    
    // Check if input is an iframe
    if (trimmedInput.startsWith('<iframe')) {
      // Extract href parameter from iframe src
      const hrefMatch = trimmedInput.match(/href=([^&"]+)/);
      if (hrefMatch && hrefMatch[1]) {
        // Decode the URL
        return decodeURIComponent(hrefMatch[1]);
      }
    }
    
    // If not an iframe, return as-is (assuming it's a direct URL)
    return trimmedInput;
  };

  const handleAddPost = async (e) => {
    e.preventDefault();
    
    if (!formData.embedUrl.trim()) {
      alert('Please enter a post URL or iframe code');
      return;
    }

    try {
      const extractedUrl = extractUrlFromInput(formData.embedUrl);
      
      if (!extractedUrl) {
        alert('Could not extract URL from input. Please check your iframe code or URL.');
        return;
      }

      // Get the highest order value and add 1
      const maxOrder = posts.length > 0 ? Math.max(...posts.map(p => p.order || 0)) : -1;
      
      await addDoc(collection(db, 'socialPosts'), {
        embedUrl: extractedUrl,
        platform: formData.platform,
        description: formData.description.trim(),
        createdAt: new Date().toISOString(),
        order: maxOrder + 1
      });

      setFormData({ embedUrl: '', platform: 'facebook', description: '' });
      setShowAddForm(false);
      fetchPosts();
    } catch (error) {
      console.error('Error adding post:', error);
      alert('Error adding post. Please try again.');
    }
  };

  const handleDeletePost = async (post) => {
    if (window.confirm(`Are you sure you want to delete this post?`)) {
      try {
        await deleteDoc(doc(db, 'socialPosts', post.id));
        setPosts(posts.filter(p => p.id !== post.id));
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post. Please try again.');
      }
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post.id);
    setFormData({
      embedUrl: post.embedUrl,
      platform: post.platform,
      description: post.description || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const extractedUrl = extractUrlFromInput(formData.embedUrl);
      
      if (!extractedUrl) {
        alert('Could not extract URL from input. Please check your iframe code or URL.');
        return;
      }

      await updateDoc(doc(db, 'socialPosts', editingPost), {
        embedUrl: extractedUrl,
        platform: formData.platform,
        description: formData.description.trim(),
        updatedAt: new Date().toISOString()
      });
      
      setEditingPost(null);
      setFormData({ embedUrl: '', platform: 'facebook', description: '' });
      fetchPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Error updating post. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setFormData({ embedUrl: '', platform: 'facebook', description: '' });
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const newPosts = [...posts];
    const temp = newPosts[index];
    newPosts[index] = newPosts[index - 1];
    newPosts[index - 1] = temp;
    
    await updatePostsOrder(newPosts);
  };

  const handleMoveDown = async (index) => {
    if (index === posts.length - 1) return;
    
    const newPosts = [...posts];
    const temp = newPosts[index];
    newPosts[index] = newPosts[index + 1];
    newPosts[index + 1] = temp;
    
    await updatePostsOrder(newPosts);
  };

  const updatePostsOrder = async (newPosts) => {
    try {
      // Update local state immediately for better UX
      setPosts(newPosts);
      
      // Update order in Firestore
      const updatePromises = newPosts.map((post, index) => 
        updateDoc(doc(db, 'socialPosts', post.id), { order: index })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating post order:', error);
      alert('Error updating post order. Please try again.');
      // Revert on error
      fetchPosts();
    }
  };

  const handleDragStart = (e, post, index) => {
    setDraggedPost({ post, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverPost(index);
  };

  const handleDragLeave = () => {
    setDragOverPost(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedPost || draggedPost.index === dropIndex) {
      setDraggedPost(null);
      setDragOverPost(null);
      return;
    }
    
    const newPosts = [...posts];
    const [removed] = newPosts.splice(draggedPost.index, 1);
    newPosts.splice(dropIndex, 0, removed);
    
    await updatePostsOrder(newPosts);
    
    setDraggedPost(null);
    setDragOverPost(null);
  };

  const handleDragEnd = () => {
    setDraggedPost(null);
    setDragOverPost(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading posts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Social Media Management</h1>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-[#218581] text-white rounded-md hover:bg-[#1a6964] flex items-center gap-2"
          >
            {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showAddForm ? 'Cancel' : 'Add Post'}
          </button>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editingPost) && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {editingPost ? 'Edit Post' : 'Add New Post'}
            </h2>
            <form onSubmit={editingPost ? (e) => { e.preventDefault(); handleSaveEdit(); } : handleAddPost}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform
                  </label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#218581]"
                  >
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="twitter">Twitter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Post URL or Embed Code *
                  </label>
                  <textarea
                    value={formData.embedUrl}
                    onChange={(e) => setFormData({ ...formData, embedUrl: e.target.value })}
                    placeholder="Paste either:&#10;1. Direct URL: https://www.facebook.com/...&#10;2. Full iframe embed code: <iframe src=...></iframe>"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#218581] font-mono text-sm"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Paste the post URL directly, or paste the entire iframe embed code from Facebook
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add a description for this post..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#218581]"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#218581] text-white rounded-md hover:bg-[#1a6964] flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {editingPost ? 'Save Changes' : 'Add Post'}
                  </button>
                  <button
                    type="button"
                    onClick={editingPost ? handleCancelEdit : () => setShowAddForm(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Posts List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6">
            Social Media Posts ({posts.length})
          </h2>

          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl font-medium text-gray-500">No posts added yet</p>
              <p className="text-gray-400">Add your first social media post to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post, index) => (
                <div
                  key={post.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, post, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-move ${
                    draggedPost?.index === index ? 'opacity-50' : ''
                  } ${
                    dragOverPost === index && draggedPost?.index !== index ? 'border-[#218581] border-2 bg-blue-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-1 pt-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                            index === 0 ? 'opacity-30 cursor-not-allowed' : 'text-gray-600 hover:text-[#218581]'
                          }`}
                          title="Move up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <GripVertical className="h-4 w-4 text-gray-400" />
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === posts.length - 1}
                          className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                            index === posts.length - 1 ? 'opacity-30 cursor-not-allowed' : 'text-gray-600 hover:text-[#218581]'
                          }`}
                          title="Move down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 ml-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium capitalize">
                          {post.platform}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(post.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex items-start gap-2 mb-2">
                        <a
                          href={post.embedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#218581] hover:text-[#1a6964] flex items-center gap-1 text-sm"
                        >
                          View Post <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>

                      {post.description && (
                        <p className="text-gray-600 text-sm mt-2">{post.description}</p>
                      )}

                      <p className="text-xs text-gray-400 mt-2 break-all">
                        {post.embedUrl}
                      </p>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => togglePreview(post.id)}
                        className="p-2 text-gray-600 hover:text-[#218581] hover:bg-gray-100 rounded-md transition-colors"
                        title={previewVisible[post.id] ? "Hide Preview" : "Show Preview"}
                      >
                        {previewVisible[post.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-2 text-gray-600 hover:text-[#218581] hover:bg-gray-100 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Preview Section */}
                  {previewVisible[post.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-center bg-gray-50 rounded-lg p-4">
                        <FacebookEmbed 
                          url={post.embedUrl} 
                          width={350}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How to add posts:</h3>
          <div className="space-y-3 text-sm text-blue-800">
            <div>
              <p className="font-semibold mb-1">Method 1: Copy Link (Easiest)</p>
              <p><strong>Facebook:</strong> Click the three dots on a post → "Copy link"</p>
              <p><strong>Instagram:</strong> Click the three dots on a post → "Copy link"</p>
              <p><strong>Twitter:</strong> Click the share icon → "Copy link to Tweet"</p>
            </div>
            <div>
              <p className="font-semibold mb-1">Method 2: Embed Code</p>
              <p><strong>Facebook:</strong> Click the three dots → "Embed" → Copy the entire <code className="bg-blue-100 px-1 rounded">&lt;iframe&gt;</code> code</p>
              <p className="text-xs mt-1">The system will automatically extract the URL from the iframe code</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaManagement;
